import { ATTRS, TAGS } from '../constants';
import { EntitiesManager } from '../entitiesManager';
import { Entity } from '../types';
import { DeviceElementEntity } from './DeviceElement';
import { DeviceProcessDataEntity } from './DeviceProcessData';
import { DevicePropertyEntity } from './DeviceProperty';
import { DeviceValuePresentationEntity } from './DeviceValuePresentation';

export class DeviceEntity implements Entity {
  public static fromXML(xml, matchIDs, files = [], entityManager?: EntitiesManager) {
    const attrs = xml._attributes;
    const isoId = attrs[ATTRS.Device.DeviceId];
    const id = matchIDs[isoId] ? matchIDs[isoId].id : null;

    const deviceElements = (xml[TAGS.DeviceElement] || []).map((xml) => {
      const deviceElementInfo = DeviceElementEntity.fromXML(xml, matchIDs);
      entityManager &&
        entityManager.registerEntity(deviceElementInfo.entity, deviceElementInfo.isoId);
      return deviceElementInfo.entity;
    });
    const processData = (xml[TAGS.DeviceProcessData] || []).map(
      (xml) => DeviceProcessDataEntity.fromXML(xml).entity
    );
    const properties = (xml[TAGS.DeviceProperty] || []).map(
      (xml) => DevicePropertyEntity.fromXML(xml).entity
    );
    const valuePresentations = (xml[TAGS.DeviceValuePresentation] || []).map(
      (xml) => DeviceValuePresentationEntity.fromXML(xml).entity
    );

    return {
      entity: new DeviceEntity(
        id,
        attrs[ATTRS.Device.ClientNAME],
        attrs[ATTRS.Device.DeviceStructureLabel],
        attrs[ATTRS.Device.DeviceLocalizationLabe],
        deviceElements,
        attrs[ATTRS.Device.DeviceDesignator],
        attrs[ATTRS.Device.DeviceSoftwareVersion],
        attrs[ATTRS.Device.DeviceSerialNumber],
        processData,
        properties,
        valuePresentations
      ),
      isoId,
    };
  }
  public type = TAGS.Device;

  constructor(
    public id: string,
    public clientNAME: string,
    public structureLabel: string,
    public localizationLabel: string,
    public deviceElements: DeviceElementEntity[],
    public designator?: string,
    public softwareVersion?: string,
    public serialNumber?: string,
    public processData?: DeviceProcessDataEntity[],
    public properties?: DevicePropertyEntity[],
    public valuePresentations?: DeviceValuePresentationEntity[]
  ) {}

  public toXML(xmlId: string, entitiesManager: EntitiesManager) {
    const deviceElementEntities = this.deviceElements.map((deviceElement) =>
      deviceElement.toXML(entitiesManager.registerEntity(deviceElement))
    );
    const processDataEntities = (this.processData || []).map((processData) => processData.toXML());

    const propertyEntities = (this.properties || []).map((property) => property.toXML());

    const valuePresentationEntities = (this.valuePresentations || []).map((presentation) =>
      presentation.toXML()
    );

    return {
      _attributes: {
        [ATTRS.Device.DeviceId]: xmlId,
        [ATTRS.Device.ClientNAME]: this.clientNAME,
        [ATTRS.Device.DeviceStructureLabel]: this.structureLabel,
        [ATTRS.Device.DeviceLocalizationLabe]: this.localizationLabel,
        ...(this.designator && { [ATTRS.Device.DeviceDesignator]: this.designator }),
        ...(this.softwareVersion && { [ATTRS.Device.DeviceSoftwareVersion]: this.softwareVersion }),
        ...(this.serialNumber && { [ATTRS.Device.DeviceSerialNumber]: this.serialNumber }),
      },
      [TAGS.DeviceElement]: deviceElementEntities,
      [TAGS.DeviceProcessData]: processDataEntities,
      [TAGS.DeviceProperty]: propertyEntities,
      [TAGS.DeviceValuePresentation]: valuePresentationEntities,
    };
  }

  hasDeviceElement(deviceElement: DeviceElementEntity) {
    return this.deviceElements.includes(deviceElement);
  }

  public getUniqueName() {
    return DeviceEntity.getUniqueName(
      this.clientNAME + '_' + (this.serialNumber == undefined ? 'null' : this.serialNumber)
    );
  }

  // According to ISO11783-10, ClientNAME is a globally unique. In reality, it's not always true
  // But currently we don't take into account such situations
  static getUniqueName(clientName: string) {
    return clientName;
  }
}
